import React, { ElementType, ReactNode } from 'react'
import { useMedia } from 'use-media'

import mediaQueries from '@/layout/media-queries'

export const COL_SIZE = {
  xsmall: 'xsmall',
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const

interface Props {
  /* Defines what HTML element will be rendered with Col component */
  tag?: ElementType
  /* Defines how much of grid space Col will be use for mobile media queries */
  xs?: number | string
  /* Defines how much of grid space Col will be use for tablet media queries */
  s?: number | string
  /* Defines how much of grid space Col will be use for low res laptops media queries */
  m?: number | string
  /* Defines how much of grid space Col will be use for high res laptops/tv/etc media queries */
  l?: number | string
  className?: string
  children: ReactNode
}

const Col = ({
  tag: Tag = 'div',
  xs,
  s,
  m,
  l,
  className,
  children,
  ...rest
}: Props) => {
  const isXs = useMedia({ maxWidth: mediaQueries.BASE_SMALL_SIZE - 1 })
  const isS = useMedia({ maxWidth: mediaQueries.BASE_MEDIUM_SIZE - 1 })
  const isM = useMedia({ maxWidth: mediaQueries.BASE_LARGE_SIZE - 1 })

  const getSizingForColumns = (cols: number | string) => {
    cols = Number(cols)

    if (cols < 5) return COL_SIZE.xsmall
    else if (cols >= 5 && cols <= 6) return COL_SIZE.small
    else if (cols >= 7 && cols < 12) return COL_SIZE.medium
    else return COL_SIZE.large
  }

  /**
   * Gets info about how much of a row's space Col is already using
   * and then categorize it with 1 of 4 groups (with getSizingForColumns).
   * That helpful with the case when we need to style element by window media query and column size.
   */
  const getSizing = () => {
    if (isXs) {
      return getSizingForColumns(xs || 12)
    } else if (isS) {
      return getSizingForColumns(s || xs || 12)
    } else if (isM) {
      return getSizingForColumns(m || s || xs || 12)
    } else {
      return getSizingForColumns(l || m || s || xs || 12)
    }
  }

  const getColClasses = () => {
    const colClasses = []

    if (xs) {
      colClasses.push(`col-xs-${xs}`)
    }

    if (s) {
      colClasses.push(`col-s-${s}`)
    }

    if (m) {
      colClasses.push(`col-m-${m}`)
    }

    if (l) {
      colClasses.push(`col-l-${l}`)
    }

    return colClasses.join(' ')
  }

  return (
    <Tag {...rest} className={`col ${className || ''} ${getColClasses()}`}>
      {React.Children.toArray(children).map((child) =>
        React.isValidElement<{ sizing: string }>(child)
          ? React.cloneElement(child, {
              sizing: getSizing(),
            })
          : null
      )}
    </Tag>
  )
}

export default Col
